import type {
  MixSheetConfiguration,
  MixSheetRecipe,
  SingleStickerConfiguration,
  StickerConfiguration,
  StickerTextLines,
  LockedStickerArtifactReference,
  SizeId,
} from "./types";
import { FONTS } from "./options";
import { isSupportedStickerSizeId } from "./size-contract";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Fields that can appear on a validation error. */
export type ValidationErrorField =
  | keyof StickerConfiguration
  | "line1"
  | "line2"
  | "line3"
  | "size"
  | "mix_variant"
  | "mix_locked_artifact_missing";

/** A single validation failure for a specific field. */
export interface ValidationError {
  field: ValidationErrorField;
  /** Stable dot-separated code for i18n lookup (e.g. "line1.required"). */
  code: string;
  /** English fallback message — replaced by i18n in task 012. */
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const LINE_MAX_LENGTH = 16;

export const QUANTITY_MIN = 1;
export const QUANTITY_MAX = 100;

const CONTROL_CHAR_RE = /[\x00-\x1F\x7F]/;

/**
 * Upper bound for asset ids (backgrounds, icons). The integration layer's
 * `resolveBackgroundById` / `resolveIconById` is authoritative for "does this id exist
 * right now" — the domain only checks that the value is structurally a plausible id.
 */
const ASSET_ID_MAX_LENGTH = 64;

function isStructurallyValidAssetId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= ASSET_ID_MAX_LENGTH;
}

// ---------------------------------------------------------------------------
// Line normalisation
// ---------------------------------------------------------------------------

/**
 * Canonical form of a line value: edge whitespace removed.
 *
 * Every path that *commits* a line — the configurator's blur handler, the
 * add-to-cart server action, the render routes reading a configuration back —
 * runs values through this, so what reaches Shopify and the print pipeline is
 * what gets printed. A stored `" Anna"` would render with a leading space on
 * the physical sticker.
 *
 * Deliberately not called on every keystroke: trimming mid-typing makes it
 * impossible to type a space between two words.
 */
export function normalizeLine(value: string | undefined | null): string {
  return typeof value === "string" ? value.trim() : "";
}

/** `normalizeLine` across all three lines. */
export function normalizeLines(
  lines: Partial<StickerTextLines> | undefined | null,
): StickerTextLines {
  return {
    line1: normalizeLine(lines?.line1),
    line2: normalizeLine(lines?.line2),
    line3: normalizeLine(lines?.line3),
  };
}

/**
 * A configuration with its lines in canonical form. Apply this at every
 * boundary where a configuration is committed or read back from storage —
 * the server actions and the render routes — so a value that reached the URL
 * without passing the configurator's blur handler still prints correctly.
 */
export function normalizeConfigurationLines<
  T extends Partial<SingleStickerConfiguration> | Partial<MixSheetConfiguration>,
>(configuration: T): T {
  return { ...configuration, lines: normalizeLines(configuration.lines) };
}

// ---------------------------------------------------------------------------
// Line validation helpers
// ---------------------------------------------------------------------------

type LineField = "line1" | "line2" | "line3";

function validateLine(
  value: string | undefined | null,
  field: LineField,
  required: boolean,
  errors: ValidationError[],
): void {
  if (value === undefined || value === null || value === "") {
    if (required) {
      errors.push({
        field,
        code: `${field}.required`,
        message: `${field} is required.`,
      });
    }
    return;
  }

  // Edge whitespace is normalised away, never reported. Mobile keyboards append
  // a trailing space whenever an autocorrect suggestion is accepted, and asking
  // the buyer to delete it is asking them to do work the app can do itself.
  // `normalizeLines` is the canonical form every commit path writes; validating
  // the trimmed value here keeps this validator agreeing with it even when a
  // caller forgets.
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    if (required) {
      errors.push({
        field,
        code: `${field}.required`,
        message: `${field} must not be blank.`,
      });
    }
    return;
  }

  if (trimmed.length > LINE_MAX_LENGTH) {
    errors.push({
      field,
      code: `${field}.too_long`,
      message: `${field} must be ${LINE_MAX_LENGTH} characters or fewer.`,
    });
  } else if (CONTROL_CHAR_RE.test(trimmed)) {
    errors.push({
      field,
      code: `${field}.invalid_characters`,
      message: `${field} contains invalid characters.`,
    });
  }
}

// ---------------------------------------------------------------------------
// Shared design field validation (common to single and mix)
// ---------------------------------------------------------------------------

function validateDesignFields(
  config: Partial<Pick<SingleStickerConfiguration, "lines" | "font" | "background" | "textColor" | "icon" | "quantity">>,
  errors: ValidationError[],
): void {
  // --- lines (all three lines optional; icon-only and background-only stickers are allowed) ---
  if (config.lines === undefined || config.lines === null) {
    errors.push({ field: "lines", code: "lines.required", message: "Text lines are required." });
  } else {
    const lines = config.lines as Partial<StickerTextLines>;
    validateLine(lines.line1, "line1", false, errors);
    validateLine(lines.line2, "line2", false, errors);
    validateLine(lines.line3, "line3", false, errors);
  }

  // --- font ---
  if (!config.font) {
    errors.push({ field: "font", code: "font.required", message: "Font is required." });
  } else if (!FONTS.some((f) => f.id === config.font)) {
    errors.push({
      field: "font",
      code: "font.unknown",
      message: `Unknown font: "${config.font}".`,
    });
  }

  // --- background ---
  if (config.background === undefined || config.background === null || config.background === "") {
    errors.push({
      field: "background",
      code: "background.required",
      message: "Background is required.",
    });
  } else if (!isStructurallyValidAssetId(config.background)) {
    errors.push({
      field: "background",
      code: "background.unknown",
      message: "Background id is invalid.",
    });
  }

  // --- textColor ---
  if (config.textColor === undefined || config.textColor === null || config.textColor === "") {
    errors.push({
      field: "textColor",
      code: "textColor.required",
      message: "Text color is required.",
    });
  } else if (!isStructurallyValidAssetId(config.textColor)) {
    errors.push({
      field: "textColor",
      code: "textColor.unknown",
      message: "Text color id is invalid.",
    });
  }

  // --- icon (optional) ---
  if (config.icon !== undefined && config.icon !== null) {
    if (!isStructurallyValidAssetId(config.icon)) {
      errors.push({
        field: "icon",
        code: "icon.unknown",
        message: "Icon id is invalid.",
      });
    }
  }

  // --- quantity ---
  if (config.quantity === undefined || config.quantity === null) {
    errors.push({
      field: "quantity",
      code: "quantity.required",
      message: "Sheet count is required.",
    });
  } else if (
    !Number.isInteger(config.quantity) ||
    config.quantity < QUANTITY_MIN ||
    config.quantity > QUANTITY_MAX
  ) {
    errors.push({
      field: "quantity",
      code: "quantity.out_of_range",
      message: `Sheet count must be a whole number between ${QUANTITY_MIN} and ${QUANTITY_MAX}.`,
    });
  }
}

// ---------------------------------------------------------------------------
// Per-size design constraints
// ---------------------------------------------------------------------------

/**
 * Data-shape mirror of the Shopify-backed design constraints. Lives here so the
 * domain layer does not import from the integrations layer — callers thread the
 * catalog-derived map in, keeping the validator pure.
 */
export interface SizeDesignConstraints {
  maxLines: 1 | 2 | 3;
  allowIcon: boolean;
}

export type SizeConstraintsContext = Partial<Record<SizeId, SizeDesignConstraints>>;

// ---------------------------------------------------------------------------
// Single configuration validation
// ---------------------------------------------------------------------------

function validateSingleConfiguration(
  config: Partial<SingleStickerConfiguration>,
  sizeConstraints?: SizeConstraintsContext,
): ValidationResult {
  const errors: ValidationError[] = [];

  validateDesignFields(config, errors);

  // --- size ---
  if (!config.size) {
    errors.push({ field: "size", code: "size.required", message: "Size is required." });
  } else if (!isSupportedStickerSizeId(config.size)) {
    errors.push({
      field: "size",
      code: "size.unknown",
      message: `Unknown size: "${config.size}".`,
    });
  }

  // --- per-size design constraints (optional, caller-supplied) ---
  if (config.size && isSupportedStickerSizeId(config.size) && sizeConstraints) {
    const constraints = sizeConstraints[config.size];
    if (constraints) {
      const lines = (config.lines ?? {}) as Partial<StickerTextLines>;
      const hasLine2 = typeof lines.line2 === "string" && lines.line2.trim().length > 0;
      const hasLine3 = typeof lines.line3 === "string" && lines.line3.trim().length > 0;

      if (constraints.maxLines < 2 && hasLine2) {
        errors.push({
          field: "line2",
          code: "line2.not_allowed_for_size",
          message: "The selected size does not allow line 2.",
        });
      }

      if (constraints.maxLines < 3 && hasLine3) {
        errors.push({
          field: "line3",
          code: "line3.not_allowed_for_size",
          message: "The selected size does not allow line 3.",
        });
      }

      if (!constraints.allowIcon && config.icon !== undefined && config.icon !== null) {
        errors.push({
          field: "icon",
          code: "icon.not_allowed_for_size",
          message: "The selected size does not allow an icon.",
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Mix configuration validation
// ---------------------------------------------------------------------------

function validateMixConfiguration(
  config: Partial<MixSheetConfiguration>,
  context?: {
    recipe?: MixSheetRecipe;
    lockedArtifactsBySize?: Partial<Record<SizeId, LockedStickerArtifactReference>>;
  },
): ValidationResult {
  const errors: ValidationError[] = [];

  validateDesignFields(config, errors);

  // --- mixVariantId ---
  if (!config.mixVariantId || config.mixVariantId.trim().length === 0) {
    errors.push({
      field: "mix_variant",
      code: "mix_variant.required",
      message: "Mix variant id is required.",
    });
  }

  // --- locked artifacts completeness (when recipe and bySize are provided) ---
  if (context?.recipe && context?.lockedArtifactsBySize !== undefined) {
    for (const item of context.recipe.items) {
      if (!context.lockedArtifactsBySize[item.sizeId]) {
        errors.push({
          field: "mix_locked_artifact_missing",
          code: "mix_locked_artifact_missing",
          message: `Locked artifact missing for recipe size "${item.sizeId}".`,
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// validateConfiguration
// ---------------------------------------------------------------------------

/**
 * Domain-level validation for a (possibly partial) StickerConfiguration.
 * Pure and synchronous — no side effects.
 *
 * For mix configurations, pass a context with `recipe` and `lockedArtifactsBySize`
 * to also validate that every recipe sizeId has a corresponding locked artifact.
 *
 * For single configurations, pass `sizeConstraints` to enforce per-size design
 * limits (maxLines / allowIcon). Mix configurations ignore `sizeConstraints` —
 * the snapshot always carries the full design and per-size filtering happens in
 * the renderer.
 */
export function validateConfiguration(
  config: Partial<SingleStickerConfiguration> | Partial<MixSheetConfiguration>,
  context?: {
    recipe?: MixSheetRecipe;
    lockedArtifactsBySize?: Partial<Record<SizeId, LockedStickerArtifactReference>>;
    sizeConstraints?: SizeConstraintsContext;
  },
): ValidationResult {
  if (config.kind === "mix") {
    return validateMixConfiguration(config as Partial<MixSheetConfiguration>, context);
  }
  return validateSingleConfiguration(
    config as Partial<SingleStickerConfiguration>,
    context?.sizeConstraints,
  );
}

// ---------------------------------------------------------------------------
// isValidConfiguration
// ---------------------------------------------------------------------------

/** Type guard — narrows a partial configuration to the full StickerConfiguration union. */
export function isValidConfiguration(
  config: Partial<SingleStickerConfiguration> | Partial<MixSheetConfiguration>,
  context?: {
    recipe?: MixSheetRecipe;
    lockedArtifactsBySize?: Partial<Record<SizeId, LockedStickerArtifactReference>>;
    sizeConstraints?: SizeConstraintsContext;
  },
): config is StickerConfiguration {
  return validateConfiguration(config, context).valid;
}
